/**
 * Design tokens for spacing
 * Using a consistent 4px base unit system
 */

export const spacing = {
  0: '0px',
  px: '1px',
  0.5: '0.125rem', // 2px
  1: '0.25rem', // 4px
  1.5: '0.375rem', // 6px
  2: '0.5rem', // 8px
  2.5: '0.625rem', // 10px
  3: '0.75rem', // 12px
  3.5: '0.875rem', // 14px
  4: '1rem', // 16px
  5: '1.25rem', // 20px
  6: '1.5rem', // 24px
  7: '1.75rem', // 28px
  8: '2rem', // 32px
  9: '2.25rem', // 36px
  10: '2.5rem', // 40px
  11: '2.75rem', // 44px
  12: '3rem', // 48px
  14: '3.5rem', // 56px
  16: '4rem', // 64px
  20: '5rem', // 80px
  24: '6rem', // 96px
  28: '7rem', // 112px
  32: '8rem', // 128px
  36: '9rem', // 144px
  40: '10rem', // 160px
  44: '11rem', // 176px
  48: '12rem', // 192px
  52: '13rem', // 208px
  56: '14rem', // 224px
  60: '15rem', // 240px
  64: '16rem', // 256px
  72: '18rem', // 288px
  80: '20rem', // 320px
  96: '24rem', // 384px
} as const

// Component-specific spacing
export const componentSpacing = {
  // Button spacing
  button: {
    paddingX: {
      xs: spacing[2],
      sm: spacing[3],
      md: spacing[4],
      lg: spacing[6],
      xl: spacing[8],
    },
    paddingY: {
      xs: spacing[1],
      sm: spacing[1.5],
      md: spacing[2],
      lg: spacing[2.5],
      xl: spacing[3],
    },
    gap: spacing[2],
  },

  // Input spacing
  input: {
    paddingX: {
      sm: spacing[2.5],
      md: spacing[3],
      lg: spacing[4],
    },
    paddingY: {
      sm: spacing[1.5],
      md: spacing[2],
      lg: spacing[2.5],
    },
  },

  // Card spacing
  card: {
    padding: {
      sm: spacing[4],
      md: spacing[6],
      lg: spacing[8],
    },
    gap: spacing[4],
  },

  // Modal spacing
  modal: {
    padding: spacing[6],
    gap: spacing[4],
  },

  // Layout spacing
  layout: {
    containerPadding: {
      sm: spacing[4],
      md: spacing[6],
      lg: spacing[8],
    },
    sectionGap: {
      sm: spacing[8],
      md: spacing[12],
      lg: spacing[16],
    },
    contentGap: {
      sm: spacing[4],
      md: spacing[6],
      lg: spacing[8],
    },
  },
} as const

// Border radius tokens
export const borderRadius = {
  none: '0px',
  sm: '0.125rem', // 2px
  base: '0.25rem', // 4px
  md: '0.375rem', // 6px
  lg: '0.5rem', // 8px
  xl: '0.75rem', // 12px
  'xxl': '1rem', // 16px
  '3xl': '1.5rem', // 24px
  full: '9999px',
} as const

// Shadow tokens
export const boxShadow = {
  none: 'none',
  sm: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
  base: '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)',
  md: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
  lg: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
  xl: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)',
  'xxl': '0 25px 50px -12px rgb(0 0 0 / 0.25)',
  inner: 'inset 0 2px 4px 0 rgb(0 0 0 / 0.05)',
} as const

export type Spacing = keyof typeof spacing
export type BorderRadius = keyof typeof borderRadius
export type BoxShadow = keyof typeof boxShadow
